import { z } from 'zod';

/**
 * Common schema primitives and query helpers shared across features.
 * Per-feature schemas live in their own files under this folder.
 */

// cuid2 ids are 24 chars of [0-9a-z]
export const cuid = z.string().regex(/^[0-9a-z]{24}$/, 'must be a cuid2 identifier');

/**
 * Money/quantity values are transported as strings to preserve precision
 * (Prisma Decimal / Postgres numeric). Length is capped so a pathological
 * input can't force expensive big-number parsing downstream (security audit
 * L5). `decimalString` allows a leading minus; `nonNegativeDecimalString`
 * does not.
 */
export const decimalString = z
  .string()
  .max(32)
  .regex(/^-?\d+(\.\d+)?$/, 'Expected decimal string');

export const nonNegativeDecimalString = z
  .string()
  .max(32)
  .regex(/^\d+(\.\d+)?$/, 'Expected non-negative decimal string');

/**
 * Optional cuid for request schemas where forms send an empty string to
 * represent "no selection" (e.g. an unset Radix Select). Empty strings are
 * coerced to `undefined` so the API never sees an invalid cuid.
 */
export const optionalCuid = () =>
  z
    .string()
    .cuid()
    .optional()
    .or(z.literal('').transform(() => undefined));

/**
 * Optional money value for request schemas where a form sends an empty string
 * for "left blank". Coerced to undefined so the API can still tell "not
 * entered" from a deliberate 0.00.
 */
export const optionalMoney = () =>
  nonNegativeDecimalString.optional().or(z.literal('').transform(() => undefined));

/**
 * Optional ISO-8601 datetime that also accepts an empty string from form
 * inputs (date pickers default to ''), coerced to undefined.
 */
export const optionalDateTime = () =>
  z
    .string()
    .datetime()
    .optional()
    .or(z.literal('').transform(() => undefined));

export const isoDateTime = z.string().datetime({ offset: true });

// Offset pagination: page is 1-based, pageSize capped to keep responses bounded.
export const paginationQuery = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export type PaginationQuery = z.infer<typeof paginationQuery>;

// `sort=field:asc` / `sort=field:desc`
export const sortParam = z
  .string()
  .regex(/^[a-zA-Z][a-zA-Z0-9_]*:(asc|desc)$/, 'expected "field:asc" or "field:desc"')
  .optional();

export const paginatedResponse = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
  });

// Every row in one response, no pages — for lists whose consumer needs all of
// them. A project's line items are the case: the project page sorts them into
// rooms and adds up each room client-side, so a page of them hides rows (and
// their money) without any error, while the BOM and the quote still count them.
export const allItemsResponse = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
  });
